import { useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { passwordStrength } from "@/lib/password";
import { cn } from "@/lib/utils";

/**
 * Ô nhập mật khẩu dùng chung cho cả 4 màn (đăng nhập, đăng ký, đổi/thêm, đặt lại).
 *
 * ⚠️ Nút hiện/ẩn là `type="button"`: trong `<form>` thì nút không khai type mặc định
 * là submit, nên bấm "xem mật khẩu" sẽ gửi luôn cả form — lỗi cổ điển và rất khó
 * nhận ra vì form vẫn "chạy", chỉ là chạy sớm một nhịp.
 *
 * ⚠️ `autoComplete` phải khai đúng ở từng chỗ dùng (`current-password` /
 * `new-password`), nếu không trình quản lý mật khẩu sẽ điền mật khẩu CŨ vào ô
 * "mật khẩu mới" rồi người dùng bấm lưu mà không nhìn.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  placeholder,
  disabled,
  showStrength = false,
  hint,
  autoFocus,
  id,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password" | "off";
  placeholder?: string;
  disabled?: boolean;
  showStrength?: boolean;
  hint?: string;
  autoFocus?: boolean;
  id?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [visible, setVisible] = useState(false);
  const strength = passwordStrength(value);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId} className="text-[11px] uppercase tracking-wider text-primary/85">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={inputId}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className="pr-10"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground transition hover:text-foreground"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {showStrength && value ? (
        <div className="flex items-center gap-2">
          <div className="flex h-1 flex-1 gap-1" aria-hidden>
            {[1, 2, 3].map((step) => (
              <span
                key={step}
                className={cn(
                  "h-full flex-1 rounded-full",
                  strength.score >= step ? strength.className : "bg-muted",
                )}
              />
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground">{strength.label}</span>
        </div>
      ) : null}
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
